import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, DataTable, Badge, Button, StatusMessage, Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Info, ArrowRight, AlertTriangle } from 'lucide-react';
import { getProviderActivity } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { formatRelativeTime } from '../../utils/formatRelativeTime.js';

/** Event-type → Badge variant (lifecycle categories; distinct from health/audit). */
const EVENT_VARIANT = {
  integration_added: 'success',
  instance_created: 'informative',
  credentials_rotated: 'alert',
  instance_removed: 'neutral',
};

/**
 * Connected Activity body: owns the fetch so loading starts as true on mount
 * (avoids setState-in-effect when the tab gates not-connected separately).
 */
function ConnectedActivity({ provider }) {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getProviderActivity(provider.slug)
      .then((res) => {
        if (cancelled) return;
        setEvents(validateApiResponse(res, t('common.requestFailed')).body ?? []);
      })
      .catch((err) => !cancelled && setError(err?.message ?? t('common.requestFailed')))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [provider.slug, t]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) {
    return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;
  }

  const firstInstance = provider.instances?.[0];

  const columns = [
    {
      key: 'timestamp',
      label: t('integrations.provider.activity.columns.timestamp'),
      render: (value) => formatRelativeTime(value, t) || '—',
    },
    {
      key: 'event_type',
      label: t('integrations.provider.activity.columns.event'),
      render: (value) => (
        <Badge variant={EVENT_VARIANT[value] ?? 'neutral'} size="small" radius="full">
          {t(`integrations.provider.activity.events.${value}`)}
        </Badge>
      ),
    },
    { key: 'target', label: t('integrations.provider.activity.columns.target') },
    { key: 'actor', label: t('integrations.provider.activity.columns.actor') },
    {
      key: 'open',
      label: '',
      render: (_value, row) =>
        row.target_instance_id ? (
          <Button
            variant="tertiary"
            size="sm"
            rightIcon={ArrowRight}
            // "instances/:id" is a sibling of the "activity" leaf route, not a
            // child of it — go up a level first, or relative resolution nests
            // it under /activity/instances/… and dead-ends on the tab bar's
            // catch-all redirect (same pitfall fixed on Overview's row CTA).
            onClick={() => navigate(`../instances/${row.target_instance_id}`)}
          >
            {t('integrations.provider.activity.openInstance')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card>
      <Card.Body className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-content">
            {t('integrations.provider.activity.title')}
          </h3>
          <span className="text-xs text-content-faint">
            {t('integrations.provider.activity.scope')}
          </span>
        </div>

        <div className="flex flex-wrap items-start gap-2 rounded-lg border border-[rgba(33,115,255,0.28)] bg-[rgba(33,115,255,0.08)] px-3 py-2.5">
          <Info size={16} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-content-secondary">
            {t('integrations.provider.activity.banner')}{' '}
            {firstInstance ? (
              <button
                type="button"
                className="inline-flex items-center gap-0.5 font-medium text-accent hover:underline"
                onClick={() => navigate(`../instances/${firstInstance.id}`)}
              >
                {t('integrations.provider.activity.openInstanceNamed', {
                  name: firstInstance.name,
                })}
                <ArrowRight size={13} aria-hidden="true" />
              </button>
            ) : null}
          </p>
        </div>

        {/* Default variant, not `workspace`: the latter pins cells to a fixed
            60px height, which reads too tall for this dense lifecycle log. */}
        <DataTable columns={columns} data={events} />

        <p className="text-xs text-content-faint">{t('integrations.provider.activity.footer')}</p>
      </Card.Body>
    </Card>
  );
}

/**
 * Provider Detail — Activity tab (CT-4183, admin-only). Provider-level lifecycle
 * events (macro history), distinct from per-instance execution logs (CT-4189)
 * and config audit (CT-4184). Newest first. Not-connected shows a locked empty
 * (no fetch); connected renders the prototype Card + DataTable.
 */
const ActivityTab = () => {
  const { t } = useTranslation('admin-studio');
  const { provider, onConnect } = useOutletContext();

  if (!provider.isConnected) {
    return (
      <div className="rounded-[10px] border border-dashed border-edge bg-surface px-6 py-12 text-center">
        <h3 className="mb-1.5 text-sm font-semibold text-content">
          {t('integrations.provider.activity.lockedTitle')}
        </h3>
        <p className="mb-3.5 text-xs text-content-secondary">
          {t('integrations.provider.activity.lockedBody')}
        </p>
        <Button variant="classicPrimary" size="sm" onClick={onConnect}>
          {t('integrations.provider.connect', { name: provider.name })}
        </Button>
      </div>
    );
  }

  return <ConnectedActivity provider={provider} />;
};

export default ActivityTab;
